import { useMemo, useState } from "preact/hooks"
import { Button } from "@/components/ui/button"
import { Checkbox } from "@/components/ui/checkbox"
import { AutoSplitCandidate } from "./auto_split_wizard_types"

interface AutoSplitMergePanelProps {
  candidates: AutoSplitCandidate[]
  onMergeCandidates: (candidateIds: string[]) => void
}

function statusColor(status: string): string {
  if (status === "matched") return "text-green-700 bg-green-50 border-green-200"
  if (status === "fallback") return "text-amber-700 bg-amber-50 border-amber-200"
  return "text-red-700 bg-red-50 border-red-200"
}

export function AutoSplitMergePanel({ candidates, onMergeCandidates }: AutoSplitMergePanelProps) {
  const [selectedIds, setSelectedIds] = useState<string[]>([])

  const selectedTotalCents = useMemo(() => {
    return candidates
      .filter((candidate: AutoSplitCandidate) => selectedIds.includes(candidate.id))
      .reduce((sum, candidate) => sum + candidate.source_amount_cents, 0)
  }, [candidates, selectedIds])

  function toggleSelected(id: string, checked: boolean) {
    setSelectedIds((prev) => (checked ? [...prev, id] : prev.filter((existingId) => existingId !== id)))
  }

  function handleMerge() {
    if (selectedIds.length < 2) return
    onMergeCandidates(selectedIds)
    setSelectedIds([])
  }

  return (
    <div class="flex flex-col gap-2">
      <div class="rounded-md border border-blue-200 bg-blue-50 px-2 py-1.5 text-xs leading-snug text-blue-900">
        Select 2+ groups and merge when they should be one split.
      </div>
      <div class="space-y-2">
        {candidates.map((candidate: AutoSplitCandidate, index: number) => (
          <div key={candidate.id} class="rounded-md border border-gray-200 p-3">
            <div class="flex items-start justify-between gap-2">
              <div class="flex min-w-0 flex-1 items-start gap-2">
                <Checkbox
                  className="mt-0.5 shrink-0"
                  checked={selectedIds.includes(candidate.id)}
                  onCheckedChange={(checked: boolean) => toggleSelected(candidate.id, !!checked)}
                />
                <div class="min-w-0 text-xs leading-snug">
                  <div class="font-semibold text-gray-900">Group {index + 1}</div>
                  <div class="mt-0.5 text-gray-600">
                    {candidate.retailer_name || "Unknown"} • {candidate.product_line || "Unknown"}
                  </div>
                </div>
              </div>
              <div class="shrink-0 text-xs font-medium tabular-nums text-gray-700">{candidate.source_amount.toFixed(2)}</div>
            </div>

            <div class="mt-2 grid grid-cols-1 gap-2 md:grid-cols-3">
              <div class="min-w-0 rounded-md border border-gray-200 bg-gray-50 px-2 py-1.5 text-xs leading-snug">
                <div class="mb-0.5 font-semibold uppercase tracking-wide text-gray-600">Raw Retailer</div>
                <div class="break-words text-gray-900">{candidate.raw_retailer || "N/A"}</div>
              </div>
              <div class="min-w-0 rounded-md border border-gray-200 bg-gray-50 px-2 py-1.5 text-xs leading-snug">
                <div class="mb-0.5 font-semibold uppercase tracking-wide text-gray-600">Raw Product</div>
                <div class="break-words text-gray-900">{candidate.raw_product_description || "N/A"}</div>
              </div>
              <div class="min-w-0 rounded-md border border-gray-200 bg-gray-50 px-2 py-1.5 text-xs leading-snug">
                <div class="mb-0.5 font-semibold uppercase tracking-wide text-gray-600">UPC/Item Key</div>
                <div class="break-words text-gray-900">{candidate.raw_upc_key || "N/A"}</div>
              </div>
            </div>

            <div class="mt-2 grid grid-cols-1 gap-2 md:grid-cols-3">
              <div class="min-w-0">
                <span class={`inline-block max-w-full rounded-md border px-2 py-0.5 text-[11px] leading-tight ${statusColor(candidate.match_status.retailer)}`}>
                  Retailer: {candidate.match_status.retailer}
                </span>
              </div>
              <div class="min-w-0">
                <span class={`inline-block max-w-full rounded-md border px-2 py-0.5 text-[11px] leading-tight ${statusColor(candidate.match_status.product_line)}`}>
                  Product Line: {candidate.match_status.product_line}
                </span>
              </div>
              <div class="min-w-0">
                <span class={`inline-block max-w-full rounded-md border px-2 py-0.5 text-[11px] leading-tight ${statusColor(candidate.match_status.product_category)}`}>
                  Product Group: {candidate.match_status.product_category}
                </span>
              </div>
            </div>
          </div>
        ))}
      </div>
      <div class="flex flex-wrap items-center justify-between gap-2 border-t border-gray-200 pt-2">
        <div class="text-sm text-gray-600">
          Selected: {selectedIds.length} groups • {(selectedTotalCents / 100).toFixed(2)}
        </div>
        <Button onClick={handleMerge} disabled={selectedIds.length < 2}>
          Merge Selected Groups
        </Button>
      </div>
    </div>
  )
}
